import { HttpClient } from '@angular/common/http'
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, HostListener } from '@angular/core'
import { AfterViewInit } from '@angular/core'
import { Router } from '@angular/router'
import { parseISO } from 'date-fns'
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs'
import { filter, map, takeUntil } from 'rxjs/operators'
import { BoundingBox, GeoLocation, RecordListingVisitGQL, SearchListing } from '../../../../../graphql'
import { placeHolderSearchListing, searchMapCenterKey } from '../../../../constant'
import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement
import { GoogleMapsService } from '../../../../injectable/google-maps-service'
import { searchCriteriaFromStorage, UnsubscribeOnDestroy } from '../../../../utility'
import { SearchMetadata } from '../../search-metadata'

export interface MapBounds {
    bounds: {
        swLat: number
        swLng: number
        neLat: number
        neLng: number
    }
    center: {
        lat: number
        lng: number,
        zoom: number,
    }
}

function calculateSearchableBounds(bounds: google.maps.LatLngBounds) {
    const east = bounds.getNorthEast().lng()
    const west = bounds.getSouthWest().lng()
    let wrapEast = false
    let wrapWest = false

    if (east < west) {
        if (Math.abs(east) > Math.abs(west)) {
            wrapEast = true
        } else {
            wrapWest = true
        }
    }

    const swLat = bounds.getSouthWest().lat()
    let swLng = bounds.getSouthWest().lng()
    const neLat = bounds.getNorthEast().lat()
    let neLng = bounds.getNorthEast().lng()
    if (wrapEast) {
        neLng = 180
    }
    if (wrapWest) {
        swLng = -180
    }
    return { swLat, swLng, neLat, neLng }
}

@Component({
    selector: 'app-search-map',
    templateUrl: 'search-map.html',
    styleUrls: ['search-map.scss']
})
export class SearchMapComponent extends UnsubscribeOnDestroy implements AfterViewInit {
    constructor(
        _httpClient: HttpClient,
        private _googleMapsService: GoogleMapsService,
        private _recordListingVisit: RecordListingVisitGQL,
        private _router: Router
    ) {
        super()
      
    }
    options = {
        center: this.mapBoundsFromStorage().center,
        zoom: 6,
        zoomControl: true,
        mapId: 'c380b190b4343d18',
        gestureHandling: 'greedy',
        disableDefaultUI: true,
        clickableIcons: false,
        restriction: {
            latLngBounds: {
                north: 85,
                south: -85,
                east: 180,
                west: -180,
            },
            strictBounds: true,
        },
    }

    @Output() boundsChanged = new EventEmitter<MapBounds>()
    @Output() numberOfMarkersVisible = new EventEmitter<number>()
    private searchEnabled = true
    @Input() listingMapMarkers$: Observable<Array<SearchListing>> = of()
    @Input() initMapCenterOverride: {
        lat: string,
        lng: string,
        zoom: string
    }
    @Input() initMapBoundingBox: BoundingBox
    apiLoaded$ = new BehaviorSubject(false)

    infoWindowListing$ = new BehaviorSubject<SearchListing>(placeHolderSearchListing)
    infoWindowListingId$ = new BehaviorSubject<string>('')
    map: google.maps.Map
    enableSearchTimeout: ReturnType<typeof setTimeout>
    visibleMarkers: google.maps.Marker[] = []
    activeMarker: AdvancedMarkerElement

    @ViewChild('mapElem') mapElement: ElementRef
    @ViewChild('infoWindow') infoWindow: ElementRef
    @Input() hoveredListingId$ = new BehaviorSubject<string>('')
    @Input() searchMetadata$ = new ReplaySubject<SearchMetadata>(1)
    searchMetadata: SearchMetadata

    // Prevent map searching when resizing window
    @HostListener('window:resize')
    onResize() {
        if (this.enableSearchTimeout) {
            clearTimeout(this.enableSearchTimeout)
        }
        this.searchEnabled = false

        this.enableSearchTimeout = setTimeout(() => {
            this.searchEnabled = true
        }, 1000)
    }

    ngAfterViewInit() {
        const sub = this._googleMapsService
            .createMap(this.mapElement.nativeElement, this.options)
            .subscribe(map => {
                this.map = map
                google.maps.event.addListenerOnce(this.map, 'idle', () => {
                    this.configureMap()
                    this.panToLastPosition()
                    this.apiLoaded$.next(true)
                })
            })

        this._subscriptions.push(sub)

        combineLatest([this.apiLoaded$, this.listingMapMarkers$])
            .pipe(
                takeUntil(this.destroy$),
                filter(([apiLoaded, markers], _) => apiLoaded),
                map(([apiLoaded, markers], _) =>
                    this.filterVisible(markers.map(m => m.obfuscatedLocation.location)).length),
            )
            .subscribe(visibleMarkers => { 
                this.numberOfMarkersVisible.emit(visibleMarkers) 
            })

        this._subscriptions.push(this.searchMetadata$.subscribe(searchMetadata => {
            this.searchMetadata = searchMetadata
        }))
    }

    onLastMarkerRendered() {
        setTimeout(() => {
            this.clusterMarkers()
        }, 0)
    }
    
    clusterMarkers() {
        const customClusterRenderer = {

            render: ({ markers, position, count }: {   
                    markers: google.maps.marker.AdvancedMarkerElement[], 
                    position: google.maps.LatLng, count: number 
                }): google.maps.marker.AdvancedMarkerElement => {

                const clusterBounds = new google.maps.LatLngBounds()
                
                markers?.forEach(marker => {
                    if (marker.position) {
                        clusterBounds.extend(marker.position)
                    }
                })

                const markerContent = document.createElement('div');
                markerContent.className = '_map-marker-cluster';
                markerContent.textContent = count.toString()

                const setZIndex = (active = false) => active ? Number(google.maps.Marker.MAX_ZINDEX) * 2 :  Number(google.maps.Marker.MAX_ZINDEX) + count

                const clusterMarker = new google.maps.marker.AdvancedMarkerElement({
                    position: position,
                    content: markerContent,
                    zIndex: setZIndex(false)
                })

                this.hoveredListingId$.subscribe(id => {
                    if (!id) {
                        markerContent.className = '_map-marker-cluster';
                        clusterMarker.zIndex = setZIndex(false)

                    } else {
                        if (this._googleMapsService.activeMarker?.position) {
                            if (clusterBounds.contains(this._googleMapsService.activeMarker?.position)) {
                                markerContent.className = '_map-marker-cluster-selected'
                                clusterMarker.zIndex = setZIndex(true)                          
                            }
                        }
                    }
                })

                markerContent.addEventListener('mouseover', () => {
                    clusterMarker.zIndex = setZIndex(true)
                })

                markerContent.addEventListener('mouseout', () => {
                    clusterMarker.zIndex = setZIndex(false)
                })

                return clusterMarker
            }
        }

        const markers = this._googleMapsService.markers
        this._googleMapsService.initializeClusterer(this.map, markers, customClusterRenderer)
    }

    private mapBoundsFromStorage() {
        const centerRaw: string = sessionStorage.getItem(searchMapCenterKey)
        if (!centerRaw) {
            return {
                center: {
                    lat: -34.4,
                    lng: 19.6,
                    zoom: 4,
                },
                bounds: {
                    swLat: -47.1788335,
                    swLng: 16.3335213,
                    neLat: -22.1250301,
                    neLng: 38.2898954,
                }
            } as MapBounds
        }
        return JSON.parse(centerRaw)
    }

    openInfoWindow(marker: AdvancedMarkerElement, listing: SearchListing) {
        this.infoWindowListingId$.next(listing.listingId)
        this.searchEnabled = false
        // @ts-ignore
        this.map?.infoWindow?.close()
        this.infoWindowListing$.next(listing)
        const infoWindow = new google.maps.InfoWindow({
            content: this.infoWindow.nativeElement
        })

        infoWindow.open({
            anchor: marker,
            map: this.map,
            shouldFocus: false,
        })
        // @ts-ignore
        this.map.infoWindow = infoWindow
        setTimeout(() => {
            this.searchEnabled = true
        }, 2000)
    }

    coverImageUrl(listing: SearchListing) {
        const coverPhoto = listing.summary.photos.find(photo => photo.useAsCover) || listing.summary.photos[0]
        return coverPhoto?.url
    }

    closeInfoWindow() {
        // @ts-ignore
        this?.map?.infoWindow?.close()
        this.infoWindowListingId$.next('')
        // @ts-ignore
        this?.map?.infoWindow = undefined
    }

    openListingDetailsInNewTab(listingId: string) {
        const criteria = searchCriteriaFromStorage()
        const deepLink = ['/listing', listingId]
        if (criteria.checkin && criteria.checkout) {
            deepLink.push(criteria.checkin)
            deepLink.push(criteria.checkout)
        }

        console.log('Recording listing visit ', listingId, ' for search ', this.searchMetadata)
        this._subscriptions.push(this._recordListingVisit.mutate({
            input: {
                listingId: listingId,
                checkin: parseISO(criteria.checkin),
                checkout: parseISO(criteria.checkout),
                searchId: this.searchMetadata?.searchId,
                pageRank: this.searchMetadata?.pageRank(listingId),
                pageNumber: this.searchMetadata?.pageNumber,
                totalResults: this.searchMetadata?.totalResults,
            }
        }).subscribe())
        window.open(this._router.serializeUrl(this._router.createUrlTree(deepLink)), '_blank')
    }

    // Parent component calls this method
    // noinspection JSUnusedGlobalSymbols
    panTo(bounds: BoundingBox) {
        if (!this.map) { return }
        if (!bounds) { return }
        if (bounds.swLat === bounds.neLat && bounds.swLng === bounds.neLng) {
            this.panToCoords(bounds.swLat, bounds.swLng, 12)
            return
        }
        this.searchEnabled = false
        const boundLiteral = {
            south: bounds.swLat,
            west: bounds.swLng,
            north: bounds.neLat,
            east: bounds.neLng,
        }
        this.map.panToBounds(boundLiteral)
        this.map.fitBounds(boundLiteral)
        this.searchEnabled = true
    }

    panToLastPosition() {
        if (this.initMapBoundingBox) {
            this.panTo(this.initMapBoundingBox)
        } else {
            const bounds = this.mapBoundsFromStorage()
            this.panToCoords(
                parseFloat(this?.initMapCenterOverride?.lat) || bounds.center.lat, 
                parseFloat(this?.initMapCenterOverride?.lng) || bounds.center.lng, 
                parseInt(this?.initMapCenterOverride?.zoom) || bounds.center.zoom
            )
        }
    }

    panToCoords(lat: number, lng: number, zoom: number) {
        this.map.setCenter({
            lat: lat,
            lng: lng
        })  
        this.map.setZoom(zoom)
    }

    private filterVisible(locations: GeoLocation[]) {
        if (!this?.map?.getBounds()) { return locations }
        const { swLat, swLng, neLat, neLng } = calculateSearchableBounds(this.map.getBounds())
        return locations.filter(l => {
            return l.lat >= swLat
                && l.lng >= swLng
                && l.lat <= neLat
                && l.lng <= neLng
        })
    }

    private configureMap() {
        // @ts-ignore
        this.map.markers = []
        this.map.addListener('click', () => {
            this.closeInfoWindow()
        })
        this.map.addListener('bounds_changed', () => {
            const bounds = this?.map?.getBounds()
            if (!bounds) { return }
            if (!this.searchEnabled) { return }
            const { swLat, swLng, neLat, neLng } = calculateSearchableBounds(bounds)
            this.boundsChanged.next({
                bounds: {
                    swLat,
                    swLng,
                    neLat,
                    neLng,
                },
                center: {
                    lat: this.map.getBounds().getCenter().lat(),
                    lng: this.map.getBounds().getCenter().lng(),
                    zoom: this.map.getZoom()
                }
            })
        })
        this.boundsChanged.subscribe(bounds => {
            sessionStorage.setItem(searchMapCenterKey, JSON.stringify(bounds))
        })
    }
}
